'use client';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useCookieState } from 'use-cookie-state';

/**
 * Cookie configuration objects
 */
const domain = { domain: process.env.NEXT_PUBLIC_NODE_ENV };

const booleanCookie = {
  ...domain,
  decode: (value) => value === 'true'
};

const numberCookie = {
  ...domain,
  decode: (value) => Number(value)
};

export const PodcastPlayerContext = createContext();

export function PodcastPlayerContextProvider({ children }) {
  /**
   * Player visibility and type state
   */
  const [isVisible, setIsVisible] = useCookieState('podcastPlayer_visible', false, booleanCookie);
  const [isAudio, setIsAudio] = useCookieState('podcastPlayer_isAudio', true, booleanCookie);
  const [isVideo, setIsVideo] = useCookieState('podcastPlayer_isVideo', false, booleanCookie);
  const [mini, setMini] = useCookieState('podcastPlayer_mini', false, booleanCookie);

  useEffect(() => {
    // Set mini mode on mobile when player becomes visible
    if (isVisible && window.innerWidth < 1024) {
      setMini(true);
    }
  }, [isVisible, setMini]);

  /**
   * Media content and playback state
   */
  const [streamUrl, setStreamUrl] = useCookieState('podcastPlayer_streamUrl', '', domain);
  const [playing, setPlaying] = useCookieState('podcastPlayer_playing', false, booleanCookie);
  const [time, setTime] = useCookieState('podcastPlayer_time', 0, numberCookie);
  const [duration, setDuration] = useCookieState('podcastPlayer_duration', 0, numberCookie);
  const [playbackRate, setPlaybackRate] = useCookieState('podcastPlayer_playbackRate', 1.0, numberCookie);

  /**
   * Episode metadata
   */
  const [image, setImage] = useCookieState('podcastPlayer_image', '', domain);
  const [episodeNumber, setEpisodeNumber] = useCookieState('podcastPlayer_episodeNumber', 1, numberCookie);
  const [episodeTitle, setEpisodeTitle] = useCookieState('podcastPlayer_episodeTitle', '', domain);
  const [accentColor, setAccentColor] = useCookieState('podcastPlayer_accentColor', '#333', domain);
  const [slug, setSlug] = useCookieState('podcastPlayer_slug', '', domain);

  /**
   * Reset all player state to default values
   */
  const resetPlayer = () => {
    setIsVisible(false);
    setIsAudio(false);
    setIsVideo(false);
    setMini(false);

    setStreamUrl('');
    setPlaying(false);
    setTime(0);
    setDuration(0);
    setPlaybackRate(1);

    setImage('');
    setEpisodeNumber(1);
    setEpisodeTitle('');
    setAccentColor('#333');
    setSlug('');
  };



  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const value = useMemo(
    () => ({
      // Player visibility and type
      isVisible,
      setIsVisible,
      isAudio,
      setIsAudio,
      isVideo,
      setIsVideo,
      mini,
      setMini,

      // Media content and playback
      streamUrl,
      setStreamUrl,
      playing,
      setPlaying,
      time,
      setTime,
      duration,
      setDuration,
      playbackRate,
      setPlaybackRate,

      // Episode metadata
      image,
      setImage,
      episodeNumber,
      setEpisodeNumber,
      episodeTitle,
      setEpisodeTitle,
      accentColor,
      setAccentColor,
      slug,
      setSlug,

      // Actions
      resetPlayer
    }),
    [
      // Only include state values in the dependency array, not setters
      isVisible,
      isAudio,
      isVideo,
      mini,
      streamUrl,
      playing,
      time,
      duration,
      playbackRate,
      image,
      episodeNumber,
      episodeTitle,
      accentColor,
      slug
    ]
  );

  return <PodcastPlayerContext.Provider value={value}>{children}</PodcastPlayerContext.Provider>;
}

/**
 * Custom hook to access the podcast context
 */
export const usePodcastPlayer = () => useContext(PodcastPlayerContext);
